import { useQContent } from '@exam-management/common/qcontent-provider';
import { QuestionDuplicationModal } from '@exam-management/qcontent-duplication';
import type { PropsWithChildren } from '@shared/utils';
import { createContext, useCallback, useMemo, useState } from 'react';

import { PreviewSingleQuestionModal, UpdatePUseModal, QuestionExportModal } from '../../containers';

import type { QContentDetailsModalContextData } from './types';

enum QContentDetailsModalAction {
  EXPORT_QUESTION = 'EXPORT_QUESTION',
  DUPLICATE_QUESTION = 'DUPLICATE_QUESTION',
  PREVIEW_QUESTION = 'PREVIEW_QUESTION',
  UPDATE_P_USE = 'UPDATE_P_USE',
}

const QContentDetailsModalContext = createContext<QContentDetailsModalContextData | undefined>(
  undefined,
);

type QContentDetailsModalProviderProps = PropsWithChildren;

export default function QContentDetailsModalProvider(
  props: Readonly<QContentDetailsModalProviderProps>,
) {
  const { children } = props;
  const [action, setAction] = useState<QContentDetailsModalAction | null>(null);
  const { qContent } = useQContent();
  const { id: qContentId, refCode: qId } = qContent;

  const exportQuestion = useCallback(() => {
    setAction(QContentDetailsModalAction.EXPORT_QUESTION);
  }, [setAction]);

  const duplicateQuestion = useCallback(() => {
    setAction(QContentDetailsModalAction.DUPLICATE_QUESTION);
  }, [setAction]);

  const previewQuestion = useCallback(() => {
    setAction(QContentDetailsModalAction.PREVIEW_QUESTION);
  }, [setAction]);

  const updatePUse = useCallback(() => {
    setAction(QContentDetailsModalAction.UPDATE_P_USE);
  }, [setAction]);

  const handleClose = () => {
    setAction(null);
  };

  const contextValue: QContentDetailsModalContextData = useMemo(
    () => ({
      exportQuestion,
      duplicateQuestion,
      previewQuestion,
      updatePUse,
    }),
    [exportQuestion, duplicateQuestion, previewQuestion, updatePUse],
  );

  return (
    <QContentDetailsModalContext.Provider value={contextValue}>
      {children}
      <QuestionExportModal
        open={action === QContentDetailsModalAction.EXPORT_QUESTION}
        onClose={handleClose}
      />
      <QuestionDuplicationModal
        open={action === QContentDetailsModalAction.DUPLICATE_QUESTION}
        onClose={handleClose}
        qContentId={qContentId}
        qId={qId}
      />
      <PreviewSingleQuestionModal
        open={action === QContentDetailsModalAction.PREVIEW_QUESTION}
        onClose={handleClose}
      />
      <UpdatePUseModal
        open={action === QContentDetailsModalAction.UPDATE_P_USE}
        onClose={handleClose}
      />
    </QContentDetailsModalContext.Provider>
  );
}

export { QContentDetailsModalContext };
